import { articles } from '@/mocks/articles';

export const SITE_URL: string =
  import.meta.env.VITE_SITE_URL || 'https://allchinabuyspread.asia';

export const SITE_NAME = 'AllChinaBuy Spreadsheet Hub';

export const SITE_LOGO_URL =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/49750b7389edabeda6497a215e0702e5.png';

const SUPPORTED_LANGS = ['en', 'fr', 'de', 'da'];

export interface PageSeo {
  title: string;
  description: string;
  keywords: string;
}

const STATIC_SEO: Record<string, PageSeo> = {
  products: {
    title: 'AllChinaBuy Spreadsheet Product Catalog — Browse All Categories',
    description:
      'Browse the complete AllChinaBuy Spreadsheet product catalog across shoes, clothing, bags, accessories, belts, perfumes and more. Click any product to purchase on the main website.',
    keywords:
      'AllChinaBuy product catalog, AllChinaBuy products, AllChinaBuy spreadsheet products, AllChinaBuy product list, AllChinaBuy categories',
  },
  'allchinabuy-spreadsheet': {
    title: 'AllChinaBuy Spreadsheet Complete Guide 2026 — Products, Sellers & Links',
    description:
      'Complete guide to the AllChinaBuy Spreadsheet. Discover product categories, seller resources, how to search, QC guides, shipping tips and buying strategies for 2026.',
    keywords:
      'AllChinaBuy spreadsheet, AllChinaBuy spreadsheet guide, AllChinaBuy spreadsheet list, AllChinaBuy spreadsheet links, AllChinaBuy seller spreadsheet',
  },
  'allchinabuy-review': {
    title: 'AllChinaBuy Review 2026 — Pros, Cons & What to Know',
    description:
      'Independent AllChinaBuy review covering purchasing experience, shipping, warehouse processes, seller research, QC photos, and common risks. Updated for 2026.',
    keywords:
      'AllChinaBuy review, AllChinaBuy review 2026, is AllChinaBuy legit, AllChinaBuy pros and cons, AllChinaBuy buying experience',
  },
  'allchinabuy-shipping': {
    title: 'AllChinaBuy Shipping Cost & Complete Shipping Guide 2026',
    description:
      'Complete guide to AllChinaBuy shipping costs, methods, delivery times, and factors that affect international shipping. Learn how to estimate and reduce shipping expenses.',
    keywords:
      'AllChinaBuy shipping, AllChinaBuy shipping cost, AllChinaBuy delivery time, AllChinaBuy shipping methods, AllChinaBuy international shipping',
  },
  'allchinabuy-coupon': {
    title: 'AllChinaBuy Coupon & Discount Code Guide 2026',
    description:
      'Guide to finding AllChinaBuy coupons, discount codes, and promotional offers. Learn how to verify codes, spot expired offers, and save on purchases.',
    keywords:
      'AllChinaBuy coupon, AllChinaBuy discount code, AllChinaBuy promo code, AllChinaBuy coupon 2026',
  },
  'allchinabuy-warehouse': {
    title: 'AllChinaBuy Warehouse Guide — Storage, QC & Shipping Prep',
    description:
      'Complete guide to AllChinaBuy warehouse services including storage, QC inspections, parcel consolidation, and shipping preparation. Understand how warehouse processes work.',
    keywords:
      'AllChinaBuy warehouse, AllChinaBuy storage, AllChinaBuy parcel consolidation, AllChinaBuy QC',
  },
  'allchinabuy-legit': {
    title: 'Is AllChinaBuy Legit? Safety & Buying Guide 2026',
    description:
      'Independent safety guide covering AllChinaBuy legitimacy, seller risks, payment safety, QC practices, shipping risks, and common buying mistakes. Make informed decisions.',
    keywords:
      'is AllChinaBuy legit, is AllChinaBuy safe, AllChinaBuy safety, AllChinaBuy scam, AllChinaBuy risk',
  },
  about: {
    title: 'About Us | AllChinaBuy Spreadsheet Hub',
    description:
      'Learn about AllChinaBuy Spreadsheet Hub, our mission, and what we cover as an independent educational resource for buying-agent platform research.',
    keywords:
      'about AllChinaBuy Spreadsheet Hub, AllChinaBuy resource hub, AllChinaBuy guides',
  },
  contact: {
    title: 'Contact Us | AllChinaBuy Spreadsheet Hub',
    description:
      'Contact AllChinaBuy Spreadsheet Hub with questions, suggestions, or feedback. Get in touch with our editorial team.',
    keywords:
      'contact AllChinaBuy Spreadsheet Hub, AllChinaBuy contact, AllChinaBuy feedback',
  },
  'privacy-policy': {
    title: 'Privacy Policy | AllChinaBuy Spreadsheet Hub',
    description:
      'Read the AllChinaBuy Spreadsheet Hub privacy policy to understand how we collect, use, and safeguard your personal information.',
    keywords: 'AllChinaBuy privacy policy, AllChinaBuy Spreadsheet Hub privacy',
  },
  terms: {
    title: 'Terms of Service | AllChinaBuy Spreadsheet Hub',
    description:
      'Read the AllChinaBuy Spreadsheet Hub terms of service governing the use of our website and content.',
    keywords: 'AllChinaBuy terms of service, AllChinaBuy Spreadsheet Hub terms',
  },
  disclaimer: {
    title: 'Disclaimer | AllChinaBuy Spreadsheet Hub',
    description:
      'Read the AllChinaBuy Spreadsheet Hub disclaimer regarding informational content, third-party links, and transaction risks.',
    keywords: 'AllChinaBuy disclaimer, AllChinaBuy Spreadsheet Hub disclaimer',
  },
};

export function getRouteKey(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (SUPPORTED_LANGS.includes(parts[0])) {
    return parts.slice(1).join('/');
  }
  return parts.join('/');
}

export function getLang(pathname: string): string {
  const first = pathname.split('/').filter(Boolean)[0];
  return SUPPORTED_LANGS.includes(first) ? first : 'en';
}

export function resolvePageSeo(routeKey: string): PageSeo | null {
  if (!routeKey) return null;

  const staticSeo = STATIC_SEO[routeKey];
  if (staticSeo) return staticSeo;

  if (routeKey.startsWith('allchinabuy-')) {
    const slug = routeKey.slice('allchinabuy-'.length);
    const article = articles[slug];
    if (article) {
      return {
        title: article.title,
        description: article.metaDesc,
        keywords: 'AllChinaBuy spreadsheet, AllChinaBuy guide, AllChinaBuy buying guide',
      };
    }
  }

  return null;
}